// Sends a large file in parts. Each part is read from the file, sealed and uploaded on its own,
// so at most a few parts are ever in memory, whatever the file's size. The key, the IV and the
// storage key are saved before the first part goes up. After a restart the upload asks storage
// which parts it already has and sends only the rest.

import { type Api, ApiError } from "./api";
import {
  type ByteSource,
  CHUNK_BYTES,
  CHUNKS_PER_PART,
  ChunkCipher,
  cipherSize,
  MEDIA_VERSION,
  partLayout,
  rootHash,
} from "./chunked";
import type { MediaRef } from "./content";
import { type FileMeta, UploadCancelledError, type UploadProgress } from "./media";

/** What has to be kept to carry on with an upload after the app restarts. */
export type ChunkedUploadState = {
  key: string;
  file_key: string;
  iv: string;
  chunk_size: number;
  chunks_per_part: number;
  /** Plain size of the file. */
  size: number;
};

/** PUTs one part. Rejects with PartUrlExpired when storage says the URL is no longer valid. */
export type PartPutter = (
  url: string,
  body: Uint8Array<ArrayBuffer>,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
) => Promise<void>;

export class PartUrlExpired extends Error {}

/** The upload was cancelled through its signal. The same error MediaClient.send throws. */
export const UploadCancelled = UploadCancelledError;
export type UploadCancelled = UploadCancelledError;

/** The server no longer has the upload, such as one left for more than a day. Start again. */
export class UploadGone extends Error {
  constructor() {
    super("This upload had expired, so it is starting again.");
  }
}

/** The parts of the API the upload uses. */
export type MultipartApi = Pick<
  Api,
  "startMultipart" | "multipartUrls" | "multipartParts" | "completeMultipart" | "abortMultipart"
>;

function cancelled(signal?: AbortSignal): boolean {
  return Boolean(signal?.aborted);
}

function throwIfCancelled(signal?: AbortSignal): void {
  if (cancelled(signal)) throw new UploadCancelled();
}

/** XHR in a webview so progress can be shown, fetch everywhere else. Both can be cancelled. */
export const defaultPartPutter: PartPutter = (url, body, onProgress, signal) => {
  if (signal.aborted) return Promise.reject(new UploadCancelled());
  if (typeof XMLHttpRequest === "undefined") {
    return fetch(url, { method: "PUT", body, signal }).then(
      (res) => {
        if (res.status === 403) throw new PartUrlExpired();
        if (!res.ok) throw new Error(`Upload failed (${res.status})`);
        onProgress(body.byteLength);
      },
      (err) => {
        throw signal.aborted ? new UploadCancelled() : err;
      },
    );
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    signal.addEventListener("abort", abort, { once: true });
    const done = (fn: () => void) => {
      signal.removeEventListener("abort", abort);
      fn();
    };
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () =>
      done(() => {
        if (xhr.status === 403) reject(new PartUrlExpired());
        else if (xhr.status < 200 || xhr.status >= 300) reject(new Error(`Upload failed (${xhr.status})`));
        else {
          onProgress(body.byteLength);
          resolve();
        }
      });
    xhr.onerror = () => done(() => reject(new Error("The upload was interrupted.")));
    xhr.onabort = () => done(() => reject(new UploadCancelled()));
    xhr.send(body);
  });
};

export type ChunkedUploadOptions = {
  putPart?: PartPutter;
  /** Parts in flight at once for one file. */
  concurrency?: number;
  /** Tries per part before the upload fails. */
  attempts?: number;
  /** Waits between tries, in ms. Tests pass 0. */
  backoff?: (attempt: number) => number;
  chunkSize?: number;
  chunksPerPart?: number;
};

export type ChunkedUploadJob = {
  chatId: string;
  source: ByteSource;
  meta: FileMeta;
  /** Set when carrying on after a restart. */
  state?: ChunkedUploadState;
  /** Called once the upload has a storage key, before any part is sent. Save it to resume. */
  onState?: (state: ChunkedUploadState) => void | Promise<void>;
  onProgress?: UploadProgress;
  signal?: AbortSignal;
  purpose?: "import";
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Uploads chunked files through the multipart routes. */
export class ChunkedUploader {
  private readonly putPart: PartPutter;
  private readonly concurrency: number;
  private readonly attempts: number;
  private readonly backoff: (attempt: number) => number;
  private readonly chunkSize: number;
  private readonly chunksPerPart: number;

  constructor(
    private readonly api: MultipartApi,
    opts: ChunkedUploadOptions = {},
  ) {
    this.putPart = opts.putPart ?? defaultPartPutter;
    this.concurrency = Math.max(1, opts.concurrency ?? 2);
    this.attempts = Math.max(1, opts.attempts ?? 4);
    this.backoff = opts.backoff ?? ((n) => Math.min(8000, 500 * 2 ** n));
    this.chunkSize = opts.chunkSize ?? CHUNK_BYTES;
    this.chunksPerPart = opts.chunksPerPart ?? CHUNKS_PER_PART;
  }

  /** Encrypts and uploads the file. Resolves with the ref to put in the message. */
  async upload(job: ChunkedUploadJob): Promise<MediaRef> {
    const { source, signal } = job;
    throwIfCancelled(signal);
    let state = job.state;
    if (state && state.size !== source.size) throw new Error("The file changed since the upload started.");
    const chunkSize = state?.chunk_size ?? this.chunkSize;
    const chunksPerPart = state?.chunks_per_part ?? this.chunksPerPart;
    const cipher = await ChunkCipher.create(source.size, {
      chunkSize,
      ...(state ? { fileKey: state.file_key, iv: state.iv } : {}),
    });
    const total = cipherSize(cipher.layout);
    const { parts, partBytes } = partLayout(cipher.layout, chunksPerPart);

    let stored = new Set<number>();
    if (state) {
      try {
        const listed = await this.api.multipartParts(state.key);
        stored = new Set(listed.parts.map((p) => p.part));
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) throw new UploadGone();
        throw err;
      }
    } else {
      const started = await this.api.startMultipart(job.chatId, total, partBytes, job.purpose);
      if (started.parts !== parts || started.part_size !== partBytes)
        throw new Error("The server split the file differently.");
      state = {
        key: started.key,
        file_key: cipher.fileKey,
        iv: cipher.iv,
        chunk_size: chunkSize,
        chunks_per_part: chunksPerPart,
        size: source.size,
      };
      await job.onState?.(state);
    }
    const key = state.key;

    // Progress counts stored bytes. Parts storage already has count at once.
    const partSize = (p: number) => (p < parts ? partBytes : total - (parts - 1) * partBytes);
    const loaded = new Map<number, number>();
    for (const p of stored) loaded.set(p, partSize(p));
    let shown = -1;
    const report = () => {
      let sum = 0;
      for (const v of loaded.values()) sum += v;
      const fraction = total ? Math.min(1, sum / total) : 1;
      if (fraction === shown) return;
      shown = fraction;
      job.onProgress?.(fraction);
    };
    report();

    // Part URLs are fetched in batches as they are needed, and again when one expires.
    const urls = new Map<number, string>();
    const urlFor = async (p: number, fresh: boolean): Promise<string> => {
      if (!fresh) {
        const known = urls.get(p);
        if (known) return known;
      }
      const wanted: number[] = [];
      for (let n = p; n <= parts && wanted.length < 20; n++) if (!stored.has(n)) wanted.push(n);
      const { urls: got } = await this.api.multipartUrls(key, wanted);
      for (const [n, url] of Object.entries(got)) urls.set(Number(n), url);
      const url = urls.get(p);
      if (!url) throw new Error("The server did not give an upload address.");
      return url;
    };

    const digests: Uint8Array[][] = new Array(parts);
    const sendPart = async (p: number) => {
      throwIfCancelled(signal);
      // Every part is sealed, even one storage has, because its hashes go into the ref.
      const sealed = await cipher.sealPart(source, p, chunksPerPart);
      digests[p - 1] = sealed.digests;
      if (stored.has(p)) return;
      for (let attempt = 0; ; attempt++) {
        throwIfCancelled(signal);
        try {
          const url = await urlFor(p, attempt > 0);
          await this.putPart(
            url,
            sealed.body,
            (n) => {
              loaded.set(p, Math.min(n, sealed.body.byteLength));
              report();
            },
            signal ?? new AbortController().signal,
          );
          loaded.set(p, sealed.body.byteLength);
          report();
          return;
        } catch (err) {
          if (err instanceof UploadCancelled || cancelled(signal)) throw new UploadCancelled();
          if (err instanceof ApiError && err.status < 500) {
            if (err.status === 404) throw new UploadGone();
            throw err;
          }
          loaded.set(p, 0);
          if (attempt + 1 >= this.attempts) throw err;
          await sleep(this.backoff(attempt));
        }
      }
    };

    // A fixed number of workers take the next part in order, so memory stays at a few parts.
    const sendAll = async () => {
      let nextPart = 1;
      const worker = async () => {
        while (nextPart <= parts) {
          const p = nextPart++;
          await sendPart(p);
        }
      };
      const workers = Array.from({ length: Math.min(this.concurrency, parts) }, worker);
      const results = await Promise.allSettled(workers);
      const failed = results.find((r) => r.status === "rejected");
      if (failed) throw (failed as PromiseRejectedResult).reason;
    };
    await sendAll();

    for (let round = 0; ; round++) {
      throwIfCancelled(signal);
      const outcome = await this.complete(key);
      if (outcome === "done") break;
      // Storage is missing a part it had confirmed. Ask what it has and send the rest once more.
      if (round > 0) throw new Error("Some parts of this file did not arrive. Try again.");
      const listed = await this.api.multipartParts(key).catch((err) => {
        // The upload finished after all, and only the answer was lost.
        if (err instanceof ApiError && err.status === 409) return null;
        throw err;
      });
      if (!listed) break;
      stored = new Set(listed.parts.map((p) => p.part));
      loaded.clear();
      for (const p of stored) loaded.set(p, partSize(p));
      urls.clear();
      await sendAll();
    }
    job.onProgress?.(1);
    const { mime, ...rest } = job.meta;
    return {
      ...rest,
      key,
      file_key: cipher.fileKey,
      iv: cipher.iv,
      sha256: await rootHash(digests.flat()),
      mime: mime || "application/octet-stream",
      size: source.size,
      version: MEDIA_VERSION,
      chunk_size: chunkSize,
    };
  }

  private async complete(key: string): Promise<"done" | "missing"> {
    try {
      await this.api.completeMultipart(key);
      return "done";
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw new UploadGone();
      if (err instanceof ApiError && err.status === 409) return "missing";
      throw err;
    }
  }

  /** Throws away a stopped upload's stored parts. Errors are ignored: housekeeping catches it. */
  async abort(key: string): Promise<void> {
    await this.api.abortMultipart(key).catch(() => undefined);
  }
}
